import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { useRoute } from 'vue-router';

import { RouteName } from '#nora-ui/router/route-names';

/**
 * NoraChat is rendered always-mounted in `App.vue` (it bootstraps conversations
 * + chat SSE at app start), so the `/chat` route renders an empty panel outlet
 * and this flag drives whether the always-mounted NoraChat is shown.
 */
export function isChatRoute(route: RouteLocationNormalizedLoaded): boolean {
  return route.name === RouteName.Chat;
}

/**
 * A route renders content in the left-sidebar `panel` named view if any of its
 * matched records declares a `panel` component (chat, operations, groups,
 * files, notifications, settings).
 */
export function routeHasPanel(route: RouteLocationNormalizedLoaded): boolean {
  return route.matched.some(
    record => record.components != null && 'panel' in record.components,
  );
}

/**
 * The first path segment of the current route (`admin`, `chat`, `operations`,
 * …), used to drive the left navigation rail highlight; `''` at the workspace
 * root.
 */
export function rootRouteSegment(route: RouteLocationNormalizedLoaded): string {
  const [segment] = route.path.split('/').filter(Boolean);

  return segment ?? '';
}

interface RouteFlags {
  isChatRoute: ComputedRef<boolean>;
  hasPanel: ComputedRef<boolean>;
  rootSegment: ComputedRef<string>;
}

/**
 * Composable wrapping the route predicates as computed refs for use in
 * components.
 */
export function useRouteFlags(): RouteFlags {
  const route = useRoute();

  return {
    isChatRoute: computed(() => isChatRoute(route)),
    hasPanel: computed(() => routeHasPanel(route)),
    rootSegment: computed(() => rootRouteSegment(route)),
  };
}
